import { chain } from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import { ObjectTypeFieldDataType } from '../../../../common/src/domain/settings/objectType/ObjectTypeFieldDataType';
import { OpenApiRouteResultType } from '../../../../common/src/routes/openApiTypes';
import { useTranslate } from '../../mixins/translate/translate';
import { openApiFetch } from '../../store/openApiFetch';
import { mainMaterialList } from '../../utils/data/mainMaterialList';
import { trafficTypeToString } from '../../utils/data/trafficTypeList';
import { InspectionStandardTypes } from '../../utils/enums/InspectionStandardTypes';
import { joinEvenAndUnevenRows } from '../../utils/joinEvenAndUnevenRows';
import { DisplayMarkdown } from '../common/markdown/DisplayMarkdown';
import { Accordion, PanelBody, PanelHeading } from '../elements';
import { PanelSpinner } from '../spinners';
import { NullableFormattedNumber } from '../utils/NullableFormattedNumber';

function ObjectTypeFieldValueLabel(props: { value: string | number | boolean | null | undefined; dataType: ObjectTypeFieldDataType }) {
    const { value, dataType } = props;
    const translate = useTranslate();
    if (value === null || value === undefined) {
        return null;
    }
    if (dataType === ObjectTypeFieldDataType.Boolean) {
        return <>{value ? translate('common.yes') : translate('common.no')}</>;
    }
    if (dataType === ObjectTypeFieldDataType.Number) {
        return <NullableFormattedNumber value={value as number} />;
    }

    return <>{value}</>;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const ObjectDetailsGeneralData = (props: { objectId: string; inspectionStandardType?: undefined | InspectionStandardTypes }) => {
    const { objectId } = props;
    const translate = useTranslate();

    const [zxyObject, setZxyObject] = useState<OpenApiRouteResultType<'/api/objects/{objectId}', 'get'> | undefined>();
    const [objectTypeFieldValues, setObjectTypeFieldValues] = useState<
        OpenApiRouteResultType<'/api/objects/{objectId}/objectTypeFieldsValues', 'get'> | undefined
    >();
    const [objectTypeFields, setObjectTypeFields] = useState<OpenApiRouteResultType<'/api/objectTypes/{objectTypeId}/objectTypeFields', 'get'>>({
        items: [],
    });

    useEffect(() => {
        async function load() {
            const fetchedObject = await openApiFetch('/api/objects/{objectId}', 'get', { objectId }, undefined);
            setZxyObject(fetchedObject);

            const fetchedFields = await openApiFetch(
                `/api/objectTypes/{objectTypeId}/objectTypeFields`,
                'get',
                { objectTypeId: fetchedObject.objectTypeId, clientCompanyId: fetchedObject.clientCompanyId },
                undefined,
            );
            setObjectTypeFields(fetchedFields);

            const fetchedFieldValues = await openApiFetch('/api/objects/{objectId}/objectTypeFieldsValues', 'get', { objectId }, undefined);
            setObjectTypeFieldValues(fetchedFieldValues);
        }

        void load();
    }, [objectId]);

    const groupedItems = chain(objectTypeFields.items)
        .groupBy('category')
        .map((value, key) => ({ category: key, leftAndRightItems: joinEvenAndUnevenRows(value) }))
        .value();

    if (zxyObject === undefined || objectTypeFieldValues === undefined) {
        return <PanelSpinner />;
    }

    return (
        <>
            <PanelHeading text={translate('object.generalData')} icon="fa fa-book"></PanelHeading>
            <PanelBody noPadding={true} whiteBackground={true}>
                <div className="pl40 pr40 mt10">
                    <Accordion index="0" title={translate('common.remarks')}>
                        <DisplayMarkdown markdown={zxyObject.remarks} />
                    </Accordion>
                </div>

                <div className="pl40 pr40 mt10">
                    <Accordion index="1" title={translate('common.dimensions')}>
                        <ul>
                            <li>
                                <strong>{translate('object.length')}</strong>:&nbsp;
                                <NullableFormattedNumber value={zxyObject.length} />
                                {zxyObject.length ? ' m' : ''}
                            </li>
                            <li>
                                <strong>{translate('object.width')}</strong>:&nbsp;
                                <NullableFormattedNumber value={zxyObject.width} />
                                {zxyObject.width ? ' m' : ''}
                            </li>
                            <li>
                                <strong>{translate('object.height')}</strong>:&nbsp;
                                <NullableFormattedNumber value={zxyObject.attributes.height} />
                                {zxyObject.attributes.height ? ' m' : ''}
                            </li>
                            <li>
                                <strong>{translate('object.squareMeters')}</strong>:&nbsp;
                                <NullableFormattedNumber value={zxyObject.squareMeters} />
                                {zxyObject.squareMeters ? ' ㎡' : ''}
                            </li>
                            <li>
                                <strong>{translate('object.volume')}</strong>:&nbsp;
                                <NullableFormattedNumber value={zxyObject.attributes.volume} />
                                {zxyObject.attributes.volume ? ' ㎡' : ''}
                            </li>
                        </ul>
                    </Accordion>
                </div>

                <div className="pl40 pr40 mt10">
                    <Accordion index="1" title={translate('common.classification')}>
                        <ul>
                            <li>
                                <strong>{translate('object.managementOrganization')}</strong>:&nbsp;{zxyObject.managementOrganization}
                            </li>
                            <li>
                                <strong>{translate('object.area')}</strong>:&nbsp;{zxyObject.attributes.area}
                            </li>
                            <li>
                                <strong>{translate('common.province')}</strong>:&nbsp;{zxyObject.province}
                            </li>
                            <li>
                                <strong>{translate('common.municipality')}</strong>:&nbsp;{zxyObject.municipality}
                            </li>
                            <li>
                                <strong>{translate('object.complexName')}</strong>:&nbsp;{zxyObject.complexName}
                            </li>
                            <li>
                                <strong>{translate('object.complexCode')}</strong>:&nbsp;{zxyObject.complexCode}
                            </li>
                            <li>
                                <strong>{translate('object.locationDescription')}</strong>:&nbsp;{zxyObject.complexLocation}
                            </li>
                        </ul>
                    </Accordion>
                </div>

                <div className="pl40 pr40 mt10">
                    <Accordion index="1" title={translate('object.construction')}>
                        <ul>
                            <li>
                                <strong>{translate('object.replacementValue')}</strong>:&nbsp;€&nbsp;
                                <NullableFormattedNumber value={zxyObject.attributes.replacementValue} />
                            </li>
                            <li>
                                <strong>{translate('object.constructionYear')}</strong>:&nbsp;{zxyObject.constructionYear}
                            </li>
                            <li>
                                <strong>{translate('object.combinedConstructionYear')}</strong>:&nbsp;{zxyObject.attributes.combinedConstructionYear}
                            </li>
                            <li>
                                <strong>{translate('object.trafficType')}</strong>:&nbsp;{trafficTypeToString(zxyObject.trafficType)}
                            </li>

                            {props.inspectionStandardType && props.inspectionStandardType === InspectionStandardTypes.Nen2767 && (
                                <>
                                    <li>
                                        <strong>{translate('object.mainMaterial')}</strong>:&nbsp;
                                        {zxyObject.mainMaterial ? mainMaterialList.find((item) => item.value === zxyObject.mainMaterial)?.text : ''}
                                    </li>
                                    <li>
                                        <strong>Palen</strong>:&nbsp;{zxyObject.attributes.palen}
                                    </li>
                                    <li>
                                        <strong>Damwandplanken </strong>:&nbsp;{zxyObject.attributes.damwandplanken}
                                    </li>
                                    <li>
                                        <strong>Dragers</strong>:&nbsp;{zxyObject.attributes.dragers}
                                    </li>
                                    <li>
                                        <strong>Belasting</strong>:&nbsp;{zxyObject.attributes.loadClass}
                                    </li>
                                    <li>
                                        <strong>{translate('common.explanation')}</strong>:&nbsp;{zxyObject.attributes.loadClassExplanation}
                                    </li>
                                </>
                            )}
                        </ul>
                    </Accordion>
                </div>

                {groupedItems.length > 0 && (
                    <div className="ph40 mt10">
                        <Accordion index="1" title={translate('objectTypeField.additionalData')} name="objectTypeFields">
                            {groupedItems.map((category) => (
                                <Fragment key={category.category}>
                                    {category.category && <h4 className="text-muted mt10 fw600 mt20">{category.category}</h4>}
                                    <ul>
                                        {category.leftAndRightItems.map((leftAndRightItem) => (
                                            <Fragment key={leftAndRightItem.left.id}>
                                                <li>
                                                    <strong>{leftAndRightItem.left.name}:&nbsp;</strong>
                                                    <ObjectTypeFieldValueLabel
                                                        value={objectTypeFieldValues[leftAndRightItem.left.id]}
                                                        dataType={leftAndRightItem.left.dataType}
                                                    />
                                                </li>
                                                {leftAndRightItem.right && (
                                                    <li>
                                                        <strong>{leftAndRightItem.right.name}:&nbsp;</strong>
                                                        <ObjectTypeFieldValueLabel
                                                            value={objectTypeFieldValues[leftAndRightItem.right.id]}
                                                            dataType={leftAndRightItem.right.dataType}
                                                        />
                                                    </li>
                                                )}
                                            </Fragment>
                                        ))}
                                    </ul>
                                </Fragment>
                            ))}
                        </Accordion>
                    </div>
                )}
            </PanelBody>
        </>
    );
};
